// client/src/utils/standings.js

import { MATCH_STATUS, MATCH_OUTCOMES } from './matchStatus'
import {
  calculateMatchPoints,
  MINIMUM_MATCHES_FOR_ELIGIBILITY,
} from './pointsCalculation'
import { MATCH_FORMAT, normalizeMatchFormat } from '../constants/matchFormats'
import { getMatchFormatRules } from './matchFormatRules'

/**
 * Compare two player standings entries for sorting.
 * PRO_SET_8: wins → head-to-head → game differential → games won
 * TRADITIONAL: points → wins → fewest losses → set win% → game win%
 * @param {Object} a - First player stats
 * @param {Object} b - Second player stats
 * @param {boolean} isProSet - Whether using PRO_SET_8 format
 * @returns {number} Negative if a ranks higher, positive if b ranks higher
 */
function comparePlayerStandings(a, b, isProSet = false) {
  if (isProSet) {
    // 1. Wins (most wins ranks higher)
    if (b.wins !== a.wins) return b.wins - a.wins
    // 2. Head-to-head (player who beat the other more often ranks higher)
    const aBeatsB = a.headToHead[getStandingEntityKey(b)] || 0
    const bBeatsA = b.headToHead[getStandingEntityKey(a)] || 0
    if (aBeatsB !== bBeatsA) return bBeatsA - aBeatsB
    // 3. Game differential (gamesWon - gamesLost)
    const aGameDiff = a.gamesWon - a.gamesLost
    const bGameDiff = b.gamesWon - b.gamesLost
    if (bGameDiff !== aGameDiff) return bGameDiff - aGameDiff
    // 4. Most games won
    return b.gamesWon - a.gamesWon
  }
  // TRADITIONAL
  // 1. Total points
  if (b.points !== a.points) return b.points - a.points
  // 2. Total wins
  if (b.wins !== a.wins) return b.wins - a.wins
  // 3. Fewest losses
  if (a.losses !== b.losses) return a.losses - b.losses
  // 4. Set win percentage
  if (parseFloat(b.setWinPercentage) !== parseFloat(a.setWinPercentage)) {
    return parseFloat(b.setWinPercentage) - parseFloat(a.setWinPercentage)
  }
  // 5. Game win percentage
  return parseFloat(b.gameWinPercentage) - parseFloat(a.gameWinPercentage)
}

function getStandingEntityKey(stat) {
  if (stat?.entityKey) return stat.entityKey
  return `player:${stat?.player?.id ?? 'unknown'}`
}

function getPlayerDisplayName(player) {
  const first = player?.first_name?.trim?.() || ''
  const last = player?.last_name?.trim?.() || ''
  const fullName = `${first} ${last}`.trim()
  return fullName || `Player #${player?.id ?? 'Unknown'}`
}

/**
 * Calculate standings from roster and matches data
 * @param {Array} roster - Array of player objects
 * @param {Array} matches - Array of match objects
 * @param {Object} options - Configuration options
 * @param {string} options.scope - 'division' or 'league'
 * @param {number} options.currentPlayerId - ID of current player (for highlighting)
 * @param {string} options.divisionFilter - Filter matches by division (for league scope)
 * @param {string} options.matchFormat - Match format ('TRADITIONAL' or 'PRO_SET_8')
 * @returns {Array} Sorted array of player standings
 */

export function calculateStandings(roster, matches, options = {}) {
  const {
    scope = 'division',
    currentPlayerId = null,
    divisionFilter = null,
    matchFormat = 'TRADITIONAL',
    minimumMatchesForEligibility = MINIMUM_MATCHES_FOR_ELIGIBILITY,
  } = options

  if (!roster || !Array.isArray(roster)) {
    return []
  }

  if (!matches || !Array.isArray(matches)) {
    return []
  }

  const normalizedFormat = normalizeMatchFormat(matchFormat)
  const isProSet = normalizedFormat === MATCH_FORMAT.PRO_SET_8
  const rules = getMatchFormatRules(normalizedFormat)
  const isTeamStandings = roster.some((player) => player?.team_id != null)

  // Initialize participant statistics (player for singles, team for doubles)
  const playerStats = {}
  const participantByPlayerId = {}

  if (isTeamStandings) {
    const teams = new Map()

    roster.forEach((player) => {
      const teamId = player?.team_id
      if (!teamId) return
      if (!teams.has(teamId)) {
        teams.set(teamId, [])
      }
      teams.get(teamId).push(player)
    })

    teams.forEach((teamPlayers, teamId) => {
      const sortedPlayers = [...teamPlayers].sort((a, b) => a.id - b.id)
      const teamLabel = sortedPlayers.map(getPlayerDisplayName).join(' / ')
      const representativePlayerId = sortedPlayers[0]?.id
      const entityKey = `team:${teamId}`

      playerStats[entityKey] = {
        player: {
          ...sortedPlayers[0],
          id: representativePlayerId,
          team_id: teamId,
          display_name: teamLabel,
        },
        entityKey,
        wins: 0,
        losses: 0,
        winPercentage: 0,
        matchesPlayed: 0,
        setsWon: 0,
        setsLost: 0,
        gamesWon: 0,
        gamesLost: 0,
        points: 0,
        headToHead: {},
        isCurrentPlayer: sortedPlayers.some((p) => p.id === currentPlayerId),
        divisionName:
          sortedPlayers.find((p) => p.division_name)?.division_name ||
          'Unknown Division',
        divisionId: sortedPlayers.find((p) => p.division_id)?.division_id,
        teamId: Number(teamId),
      }

      sortedPlayers.forEach((p) => {
        participantByPlayerId[p.id] = entityKey
      })
    })
  } else {
    roster.forEach((player) => {
      const entityKey = `player:${player.id}`
      playerStats[entityKey] = {
        player: player,
        entityKey,
        wins: 0,
        losses: 0,
        winPercentage: 0,
        matchesPlayed: 0,
        setsWon: 0,
        setsLost: 0,
        gamesWon: 0,
        gamesLost: 0,
        points: 0, // Total points earned
        headToHead: {}, // head-to-head wins: { [opponentId]: count }
        isCurrentPlayer: player.id === currentPlayerId,
        // Additional data for league view
        divisionName: player.division_name || 'Unknown Division',
        divisionId: player.division_id,
      }
      participantByPlayerId[player.id] = entityKey
    })
  }

  // Filter matches based on scope and division filter
  const filteredMatches = matches.filter((match) => {
    const status = match.status
    const scores = match.set_scores || []

    // Treat these as completed outcomes
    const isCompletedNormal =
      status === MATCH_STATUS.NORMAL && scores.length > 0
    const isCompletedRetired = MATCH_OUTCOMES.filter(
      (s) => s !== MATCH_STATUS.NORMAL,
    ).includes(status)

    if (!isCompletedNormal && !isCompletedRetired) return false

    // Apply division filter for league scope
    if (scope === 'league' && divisionFilter) {
      return match.division_id === divisionFilter
    }

    return true
  })

  // Process matches to calculate statistics
  filteredMatches.forEach((match) => {
    const status = match.status
    const scores = match.set_scores || []

    const homePlayerId = Number(match.home_player_id)
    const awayPlayerId = Number(match.away_player_id)
    const homeEntityKey =
      isTeamStandings && match.home_team_id != null
        ? `team:${match.home_team_id}`
        : participantByPlayerId[homePlayerId]
    const awayEntityKey =
      isTeamStandings && match.away_team_id != null
        ? `team:${match.away_team_id}`
        : participantByPlayerId[awayPlayerId]

    if (!homeEntityKey || !awayEntityKey || homeEntityKey === awayEntityKey) {
      return
    }

    // Aggregate helpers
    let homeSetWins = 0
    let awaySetWins = 0
    let homeGamesTotal = 0
    let awayGamesTotal = 0
    let winnerId = null
    let loserId = null

    if (status === MATCH_STATUS.NORMAL && scores.length > 0) {
      // Count actual sets/games
      scores.forEach((set) => {
        const hg = set.home_games || 0
        const ag = set.away_games || 0
        homeGamesTotal += hg
        awayGamesTotal += ag
        if (hg > ag) homeSetWins++
        else if (ag > hg) awaySetWins++
      })
      const homeWonMatch = homeSetWins > awaySetWins
      winnerId = homeWonMatch ? homeEntityKey : awayEntityKey
      loserId = homeWonMatch ? awayEntityKey : homeEntityKey
    } else {
      // Retirement/default outcomes
      // Determine winner from normalized or legacy statuses
      const subBy = Number(match.submitted_by_player_id)
      const subByEntityKey = participantByPlayerId[subBy]
      if (status === 'home_retired') {
        winnerId = awayEntityKey
        loserId = homeEntityKey
      } else if (status === 'away_retired') {
        winnerId = homeEntityKey
        loserId = awayEntityKey
      } else if (status === 'i_retired') {
        // Submitter retired — resolve via entity key so both partners are covered
        if (subByEntityKey === homeEntityKey) {
          winnerId = awayEntityKey
          loserId = homeEntityKey
        } else if (subByEntityKey === awayEntityKey) {
          winnerId = homeEntityKey
          loserId = awayEntityKey
        }
      } else if (status === MATCH_STATUS.OPPONENT_RETIRED) {
        // Opponent of submitter retired — resolve via entity key
        if (subByEntityKey === homeEntityKey) {
          winnerId = homeEntityKey
          loserId = awayEntityKey
        } else if (subByEntityKey === awayEntityKey) {
          winnerId = awayEntityKey
          loserId = homeEntityKey
        }
      }

      // If no scores, assume sets and games by convention
      // PRO_SET_8: 1-0 sets, 8-0 games; TRADITIONAL: 2-0 sets, 12-0 games
      if (scores.length === 0) {
        const defaultSets = rules.setsNeededToWin
        const defaultGames = isProSet ? 8 : 12
        if (winnerId === homeEntityKey) {
          homeSetWins = defaultSets
          awaySetWins = 0
          homeGamesTotal = defaultGames
          awayGamesTotal = 0
        } else if (winnerId === awayEntityKey) {
          homeSetWins = 0
          awaySetWins = defaultSets
          homeGamesTotal = 0
          awayGamesTotal = defaultGames
        }
      } else {
        // If scores were provided with a retirement, count actual sets played
        // then award remaining sets to the winner
        scores.forEach((set) => {
          const hg = set.home_games || 0
          const ag = set.away_games || 0
          homeGamesTotal += hg
          awayGamesTotal += ag
          if (hg > ag) homeSetWins++
          else if (ag > hg) awaySetWins++
        })
        if (winnerId == null) {
          const homeWon = homeSetWins > awaySetWins
          winnerId = homeWon ? homeEntityKey : awayEntityKey
          loserId = homeWon ? awayEntityKey : homeEntityKey
        }

        // Award remaining sets to winner so they always reach setsNeededToWin
        if (winnerId === homeEntityKey && homeSetWins < rules.setsNeededToWin) {
          homeSetWins = rules.setsNeededToWin
        } else if (
          winnerId === awayEntityKey &&
          awaySetWins < rules.setsNeededToWin
        ) {
          awaySetWins = rules.setsNeededToWin
        }
      }
    }

    // Update match statistics (only if we could infer winner)
    if (winnerId != null && loserId != null) {
      if (playerStats[winnerId]) {
        playerStats[winnerId].wins++
        playerStats[winnerId].matchesPlayed++
        // Track head-to-head record for tiebreaker
        playerStats[winnerId].headToHead[loserId] =
          (playerStats[winnerId].headToHead[loserId] || 0) + 1
      }
      if (playerStats[loserId]) {
        playerStats[loserId].losses++
        playerStats[loserId].matchesPlayed++
      }

      // Calculate points based on match result
      const winnerSetCount =
        winnerId === homeEntityKey ? homeSetWins : awaySetWins
      const loserSetCount =
        loserId === homeEntityKey ? homeSetWins : awaySetWins

      if (playerStats[winnerId]) {
        playerStats[winnerId].points += calculateMatchPoints(
          winnerSetCount,
          loserSetCount,
          true,
          normalizedFormat,
        )
      }
      if (playerStats[loserId]) {
        playerStats[loserId].points += calculateMatchPoints(
          winnerSetCount,
          loserSetCount,
          false,
          normalizedFormat,
        )
      }

      // Update set and game statistics
      if (playerStats[homeEntityKey] && playerStats[awayEntityKey]) {
        playerStats[homeEntityKey].setsWon += homeSetWins
        playerStats[homeEntityKey].setsLost += awaySetWins
        playerStats[homeEntityKey].gamesWon += homeGamesTotal
        playerStats[homeEntityKey].gamesLost += awayGamesTotal

        playerStats[awayEntityKey].setsWon += awaySetWins
        playerStats[awayEntityKey].setsLost += homeSetWins
        playerStats[awayEntityKey].gamesWon += awayGamesTotal
        playerStats[awayEntityKey].gamesLost += homeGamesTotal
      }
    }
  })

  // Calculate percentages and additional metrics
  Object.values(playerStats).forEach((stat) => {
    // Win percentage
    stat.winPercentage =
      stat.matchesPlayed > 0
        ? ((stat.wins / stat.matchesPlayed) * 100).toFixed(1)
        : 0

    // Set win percentage
    const totalSets = stat.setsWon + stat.setsLost
    stat.setWinPercentage =
      totalSets > 0 ? ((stat.setsWon / totalSets) * 100).toFixed(1) : 0

    // Game win percentage
    const totalGames = stat.gamesWon + stat.gamesLost
    stat.gameWinPercentage =
      totalGames > 0 ? ((stat.gamesWon / totalGames) * 100).toFixed(1) : 0

    // Ineligible flag for players with less than 3 matches
    stat.isIneligible = stat.matchesPlayed < minimumMatchesForEligibility
  })

  // Sort standings using format-specific comparison
  const sortedStandings = Object.values(playerStats).sort((a, b) =>
    comparePlayerStandings(a, b, isProSet),
  )

  return sortedStandings
}

/**
 * Calculate division standings from division data (backward compatibility)
 * @param {Object} division - Division object containing roster and matches
 * @returns {Array} Sorted array of player standings
 */
export function calculateDivisionStandings(division) {
  if (!division || !division.roster) {
    return []
  }

  return calculateStandings(division.roster, division.matches, {
    scope: 'division',
    currentPlayerId: division.player_id,
    minimumMatchesForEligibility: division.playoff_min_matches_required,
  })
}

/**
 * Calculate league-wide standings from league data
 * @param {Array} allPlayers - Array of all players in the league
 * @param {Array} allMatches - Array of all matches in the league
 * @param {number} currentPlayerId - ID of current player for highlighting
 * @param {number} divisionFilter - Optional division ID to filter by
 * @param {string} matchFormat - Match format ('TRADITIONAL' or 'PRO_SET_8')
 * @returns {Array} Sorted array of player standings
 */
export function calculateLeagueStandings(
  allPlayers,
  allMatches,
  currentPlayerId,
  divisionFilter = null,
  matchFormat = 'TRADITIONAL',
  minimumMatchesForEligibility = MINIMUM_MATCHES_FOR_ELIGIBILITY,
) {
  return calculateStandings(allPlayers, allMatches, {
    scope: 'league',
    currentPlayerId,
    divisionFilter,
    matchFormat,
    minimumMatchesForEligibility,
  })
}

/**
 * Group standings by division for league view
 * @param {Array} standings - Array of player standings
 * @param {string} matchFormat - Match format ('TRADITIONAL' or 'PRO_SET_8')
 * @returns {Object} Object with division IDs as keys and standings arrays as values (with divisionName for display)
 */
export function groupStandingsByDivision(
  standings,
  matchFormat = 'TRADITIONAL',
) {
  const isProSet = normalizeMatchFormat(matchFormat) === MATCH_FORMAT.PRO_SET_8
  const grouped = {}

  standings.forEach((playerStat) => {
    // Use division_id as the key to prevent merging divisions with same names
    const divisionKey = playerStat.divisionId || 'unknown'
    const divisionName = playerStat.divisionName || 'Unknown Division'

    if (!grouped[divisionKey]) {
      grouped[divisionKey] = {
        divisionId: playerStat.divisionId,
        divisionName: divisionName,
        players: [],
      }
    }

    grouped[divisionKey].players.push(playerStat)
  })

  // Sort each division's standings using format-specific comparison
  Object.keys(grouped).forEach((divisionKey) => {
    grouped[divisionKey].players.sort((a, b) =>
      comparePlayerStandings(a, b, isProSet),
    )

    // Assign ranks with tie handling for each division
    grouped[divisionKey].players = assignRanks(
      grouped[divisionKey].players,
      isProSet,
    )
  })

  return grouped
}

/**
 * Get player rank within standings
 * @param {Array} standings - Sorted array of player standings
 * @param {number} playerId - ID of player to find rank for
 * @returns {number} Player's rank (1-based) or -1 if not found
 */
export function getPlayerRank(standings, playerId) {
  if (playerId == null) return -1
  // For team/doubles entries the representative player id may not match the
  // caller's playerId (e.g. the second partner). isCurrentPlayer is already
  // set for all team members, so use it as a fallback for team entries.
  const index = standings.findIndex(
    (stat) =>
      stat.player.id === playerId ||
      (stat.teamId != null && stat.isCurrentPlayer),
  )
  return index >= 0 ? index + 1 : -1
}

/**
 * Check if two player stats are tied based on all tiebreaker criteria
 * @param {Object} a - First player stats
 * @param {Object} b - Second player stats
 * @param {boolean} isProSet - Whether using PRO_SET_8 format
 * @returns {boolean} True if players are tied on all criteria
 */
function arePlayersTied(a, b, isProSet = false) {
  if (isProSet) {
    const aBeatsB = a.headToHead[getStandingEntityKey(b)] || 0
    const bBeatsA = b.headToHead[getStandingEntityKey(a)] || 0
    return (
      a.wins === b.wins &&
      aBeatsB === bBeatsA &&
      a.gamesWon - a.gamesLost === b.gamesWon - b.gamesLost &&
      a.gamesWon === b.gamesWon
    )
  }
  return (
    a.points === b.points &&
    a.wins === b.wins &&
    a.losses === b.losses &&
    parseFloat(a.setWinPercentage) === parseFloat(b.setWinPercentage) &&
    parseFloat(a.gameWinPercentage) === parseFloat(b.gameWinPercentage)
  )
}

/**
 * Assign ranks to standings accounting for ties
 * @param {Array} standings - Sorted array of player standings
 * @param {boolean} isProSet - Whether using PRO_SET_8 format (affects tie detection)
 * @returns {Array} Standings with rank property assigned
 */
function assignRanks(standings, isProSet = false) {
  if (standings.length === 0) return []

  const rankedStandings = []
  let currentRank = 1

  for (let i = 0; i < standings.length; i++) {
    const stat = standings[i]

    // Check if tied with previous player
    if (i > 0 && arePlayersTied(stat, standings[i - 1], isProSet)) {
      // Use same rank as previous player
      rankedStandings.push({ ...stat, rank: rankedStandings[i - 1].rank })
    } else {
      // New rank (accounts for skipped ranks due to ties)
      rankedStandings.push({ ...stat, rank: currentRank })
    }

    currentRank = i + 2 // Next available rank
  }

  return rankedStandings
}

/**
 * Format standings display data for UI components
 * @param {Array} standings - Array of player standings
 * @param {Object} options - Formatting options
 * @param {boolean} options.showDivision - Include division information
 * @param {boolean} options.showSets - Include set statistics
 * @param {boolean} options.showGames - Include game statistics
 * @param {string} options.matchFormat - Match format for tie detection ('TRADITIONAL' or 'PRO_SET_8')
 * @returns {Array} Formatted standings data
 */
export function formatStandingsForDisplay(standings, options = {}) {
  const {
    showDivision = false,
    showSets = false,
    showGames = false,
    matchFormat = 'TRADITIONAL',
  } = options

  const isProSet = normalizeMatchFormat(matchFormat) === MATCH_FORMAT.PRO_SET_8

  // Assign ranks with tie handling
  const rankedStandings = assignRanks(standings, isProSet)

  return rankedStandings.map((stat) => ({
    rank: stat.rank,
    playerName:
      stat.player.display_name ||
      `${stat.player.first_name} ${stat.player.last_name}`.trim(),
    playerId: stat.player.id,
    ...(stat.teamId != null && { teamId: stat.teamId }),
    isCurrentPlayer: stat.isCurrentPlayer,
    matchesPlayed: stat.matchesPlayed,
    wins: stat.wins,
    losses: stat.losses,
    record: `${stat.wins}-${stat.losses}`,
    winPercentage: stat.winPercentage,
    points: stat.points,
    isIneligible: stat.isIneligible,
    // Optional additional data
    ...(showDivision && {
      divisionName: stat.divisionName,
      divisionId: stat.divisionId,
    }),
    ...(showSets && {
      setsWon: stat.setsWon,
      setsLost: stat.setsLost,
      setRecord: `${stat.setsWon}-${stat.setsLost}`,
      setWinPercentage: stat.setWinPercentage,
    }),
    ...(showGames && {
      gamesWon: stat.gamesWon,
      gamesLost: stat.gamesLost,
      gameRecord: `${stat.gamesWon}-${stat.gamesLost}`,
      gameWinPercentage: stat.gameWinPercentage,
    }),
  }))
}
